/**
 * The pieces every phone card's third row is made of — list engine v3 §8.
 *
 * `CardLine` itself lives in `data-list.tsx`, because the engine is what
 * draws it. This is the handful of values the five screens all need to build
 * one: the status chip, the branch, when the record last did anything.
 *
 * It is a module rather than five copies because the failure it prevents is
 * silent. Nothing breaks when Contacts prints a date as "Sep 28, 2:03 pm" and
 * Visits prints the same column as "Thu Sep 17" — the cards simply stop
 * looking like one product, and nobody can point at the line that did it.
 */
import type { ReactNode } from 'react'
import { StatusChip, humanize } from './primitives.js'
import { VISIT_STATUS_AS_JOB } from './record-list.js'
import { formatDateOnly } from '../lib/format.js'
import type { ListRow } from '../lib/use-list.js'

/** A cell value as a trimmed string, or '' — the form every part is tested in. */
export const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim()

/**
 * **When this row last did anything**, at the right-hand end of row three.
 *
 * `Sep 26`, never `Sep 26, 2:03 pm`: it shares the row with a chip and up to
 * two facts on a 390px screen, and the hour of a thing that happened three
 * weeks ago is not what anybody is scanning for. `formatDateOnly` reads the
 * first ten characters as a day, so a `datetime` column arrives here safely.
 *
 * Absent rather than an em dash when there is nothing: `lastActivityAt` is
 * `greatest(updated_at, …)` and `updated_at` is never null, so a blank here
 * means the column was not fetched — and a dash pinned to the right of every
 * card would read as a fact about the records rather than about the request.
 */
export function lastActivity(row: ListRow): ReactNode | undefined {
  const value = text(row.lastActivityAt)
  return value ? formatDateOnly(value) : undefined
}

/**
 * The job palette's chip, which visits borrow.
 *
 * `VISIT_STATUS_AS_JOB` is the mapping `list-marks.md` §2 calls "aliases of
 * the job tokens" — the same one `visitMark` uses, so the calendar card on
 * the left and the chip on the third row cannot disagree about what colour
 * a skipped visit is.
 */
export function jobStatusChip(row: ListRow): ReactNode | undefined {
  const status = text(row.status)
  if (!status) return undefined
  return <StatusChip group="job" status={status}>{humanize(status)}</StatusChip>
}

export function visitStatusChip(row: ListRow): ReactNode | undefined {
  const status = text(row.status)
  if (!status) return undefined
  return (
    <StatusChip group="job" status={VISIT_STATUS_AS_JOB[status] ?? 'scheduled'}>
      {humanize(status)}
    </StatusChip>
  )
}
